import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { User } from 'lucide-react';
import ThemeToggle from '../common/ThemeToggle';
import { ZEBU_SIGNUP_URL } from '../../config/tenantConfig';

/**
 * Shared top navigation for all PUBLIC (unauthenticated) pages — the landing
 * page and the public marketplace. Styled to the Zebu marketing design
 * (Discover Algos / Build Your Own · NEW / Open Demat Account / Login) and
 * highlights the link matching the current route with a tab-style underline.
 *
 * Self-contained: it derives the "Build Your Own" target from auth state, so
 * pages can drop it in with no props.
 */
const PublicTopNav: React.FC = () => {
  const { pathname } = useLocation();

  // Signed-in visitors go straight to the basket builder; everyone else to the
  // "Build Your Own" marketing page.
  const isAuthed = (() => {
    try {
      return !!localStorage.getItem('auth_tokens');
    } catch {
      return false;
    }
  })();
  const builderTarget = isAuthed ? '/baskets' : '/build-your-own';

  const isDiscoverActive = pathname.startsWith('/marketplace');
  const isBuilderActive = pathname.startsWith('/build-your-own') || pathname.startsWith('/baskets');

  // Full-height nav item with a tab-style active underline aligned to the
  // navbar's bottom border.
  const itemClass = (active: boolean) =>
    `h-16 flex items-center gap-1.5 text-sm font-medium border-b-2 transition-colors ${
      active
        ? 'border-zebu-600 text-zebu-600 dark:text-zebu-400 dark:border-zebu-400'
        : 'border-transparent text-gray-700 dark:text-gray-300 hover:text-zebu-600 dark:hover:text-zebu-400'
    }`;

  return (
    <nav className="sticky top-0 z-50 border-b border-gray-200/70 dark:border-gray-800 bg-white/80 dark:bg-gray-950/80 backdrop-blur-md">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src="/zebu-logo.svg" alt="Zebu Algo Trade" className="h-9 w-auto" />
          </Link>

          {/* Center nav — hidden on small screens */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/marketplace" className={itemClass(isDiscoverActive)}>
              Discover Algos
            </Link>
            <Link to={builderTarget} className={itemClass(isBuilderActive)}>
              Build Your Own
              <span className="text-[0.6rem] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-zebu-50 dark:bg-zebu-900/40 text-zebu-600 dark:text-zebu-300">
                New
              </span>
            </Link>
            <a
              href={ZEBU_SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={itemClass(false)}
            >
              Open Demat Account
            </a>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle />
            <Link
              to="/auth"
              className="flex items-center gap-1.5 text-sm font-medium px-3.5 py-2 rounded-lg border border-zebu-200 dark:border-zebu-800 text-zebu-700 dark:text-zebu-300 hover:bg-zebu-50 dark:hover:bg-zebu-900/30 transition-colors"
            >
              <User className="h-4 w-4" />
              Login
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default PublicTopNav;
